import { createSelector } from "reselect";
import { getAllSites } from "./sites";

// Dashboard site filter keys, kept in state.generalSettings.dashboardFilters.
export const WITH_ALARMS = "withAlarms";
export const WITH_ONLINE = "withOnline";

const EMPTY_FILTERS = [];

const selectGateways = (state) => state.gateways;
const selectAlerts = (state) => state.alerts;
const selectIncludeHiddenSites = (state) =>
  state.generalSettings?.includeHiddenSites ?? false;
const selectIncludeEmptySites = (state) =>
  state.generalSettings?.includeEmptySites ?? true;
const selectDashboardFilters = (state) =>
  state.generalSettings?.dashboardFilters ?? EMPTY_FILTERS;

/**
 * Sites enriched for the dashboard. Each site is a new object (the store is never mutated):
 * - gateways: the site's gateways merged with their live store entry; gateways the store
 *   doesn't know yet are dropped.
 * - alarms: one alert bucket per gateway of the site that has active alerts.
 * - offlineGateways: the site's gateways whose status is "OFF".
 * - gatewayGuids: every gateway guid the backend lists for the site.
 */
export const getDashboardSites = createSelector(
  [getAllSites, selectGateways, selectAlerts],
  (sites, gateways, alerts) => {
    const gatewaysById = gateways?.byId ?? {};
    const alertsById = alerts?.byId ?? {};

    return sites.map((site) => {
      const siteGateways = site.gateways ?? [];
      const knownGateways = siteGateways
        .filter((gw) => gatewaysById[gw.gatewayGuid] !== undefined)
        .map((gw) => ({ ...gw, ...gatewaysById[gw.gatewayGuid] }));

      return {
        ...site,
        gatewayGuids: siteGateways.map((gw) => gw.gatewayGuid),
        gateways: knownGateways,
        alarms: siteGateways
          .filter((gw) => alertsById[gw.gatewayGuid] !== undefined)
          .map((gw) => alertsById[gw.gatewayGuid]),
        offlineGateways: knownGateways.filter((gw) => gw.status === "OFF"),
      };
    });
  }
);

// The "Include ..." options of the Filters menu: they hide sites while off.
export const INCLUDE_EMPTY_SITES = "includeEmptySites";
export const INCLUDE_HIDDEN_SITES = "includeHiddenSites";

// Whether the site passes one option of the Filters menu, on its own.
const sitePassesOption = {
  [WITH_ALARMS]: (site) => site.offlineGateways.length > 0 || site.alarms.length > 0,
  [WITH_ONLINE]: (site) => site.offlineGateways.length !== site.gateways.length,
  // Empty site: the backend lists no gateways for it.
  [INCLUDE_EMPTY_SITES]: (site) => site.gatewayGuids.length > 0,
  [INCLUDE_HIDDEN_SITES]: (site) => !site.isHidden,
};

// The options of the Filters menu that currently narrow the list.
const narrowingOptions = ({ filters, includeHiddenSites, includeEmptySites }) => [
  ...filters.filter((key) => sitePassesOption[key]),
  ...(includeEmptySites ? [] : [INCLUDE_EMPTY_SITES]),
  ...(includeHiddenSites ? [] : [INCLUDE_HIDDEN_SITES]),
];

// True when the site passes every dashboard filter (search is applied separately).
export const siteMatchesDashboardFilters = (site, settings) =>
  narrowingOptions(settings).every((key) => sitePassesOption[key](site));

/**
 * The options of the Filters menu that hide at least one of `sites` (AB#50387), in
 * the menu's order of filters first, then "Include ..." options. A user who cannot
 * include hidden sites never sees them: that option is not theirs to remove.
 */
export const dashboardFiltersHidingSites = (sites, settings) => {
  const { canIncludeHiddenSites } = settings;
  return narrowingOptions(settings)
    .filter((key) => key !== INCLUDE_HIDDEN_SITES || canIncludeHiddenSites)
    .filter((key) => sites.some((site) => !sitePassesOption[key](site)));
};

// The gateways of the sites and their controllers, as YOUR INSTALLATIONS counts
// them: gateways whose devices are not loaded yet count no controller.
export const sitesContentCount = (sites) => {
  const siteGateways = sites.flatMap((site) => site.gateways);
  const controllers = siteGateways.reduce(
    (count, gw) => count + (gw.devices ?? []).filter((d) => !d.isDongleController).length,
    0
  );
  return { gateways: siteGateways.length, controllers };
};

/**
 * Dashboard sites after the Filters menu (with issues, online, hidden, empty), before the
 * search box. The site list, the map, YOUR INSTALLATIONS and ACTIVE ALARMS all start from
 * this list, so those counts always match the filters.
 */
export const getFilteredDashboardSites = createSelector(
  [
    getDashboardSites,
    selectDashboardFilters,
    selectIncludeHiddenSites,
    selectIncludeEmptySites,
  ],
  (sites, filters, includeHiddenSites, includeEmptySites) =>
    sites.filter((site) =>
      siteMatchesDashboardFilters(site, {
        filters,
        includeHiddenSites,
        includeEmptySites,
      })
    )
);
